// A2.10 / A2.11 — module-local data layer. Real admin-managed registries backed
// by migration 000250 (admin_alert_rules, admin_dashboards). No i18n/App.tsx edits.
//
//   A2.10 alert rules — CRUD over /admin/alert-rules (name, metric, threshold,
//         channel, enabled). Metric options come from the REAL live-metric
//         catalog (/admin/analytics/report, A16.40).
//   A2.11 dashboards  — CRUD over /admin/dashboards; a dashboard is a named
//         selection of live metrics (layout = JSON array of metric keys). Loading
//         a dashboard renders those metrics' current live values.
import { api } from '@/lib/api'

/* ── A2.10 alert rules ─────────────────────────────────────────────── */

export type AlertChannel = 'email' | 'telegram' | 'webhook' | 'inapp'

export interface AlertRule {
  id: number
  name: string
  metric: string
  threshold: number
  channel: AlertChannel
  enabled: boolean
  created_at: number
}

export interface AlertRuleInput {
  name: string
  metric: string
  threshold: number
  channel: AlertChannel
  enabled: boolean
}

export const fetchAlertRules = (s?: AbortSignal) =>
  api
    .get<{ rules: AlertRule[] }>('/admin/alert-rules', s ? { signal: s } : undefined)
    .then((d) => d.rules ?? [])

export const createAlertRule = (b: AlertRuleInput) =>
  api.post<{ ok: boolean; id: number }>('/admin/alert-rules', b)

export const updateAlertRule = (id: number, b: AlertRuleInput) =>
  api.put<{ ok: boolean }>(`/admin/alert-rules/${id}`, b)

export const deleteAlertRule = (id: number) => api.del<{ ok: boolean }>(`/admin/alert-rules/${id}`)

/* ── A2.11 dashboards ──────────────────────────────────────────────── */

export interface Dashboard {
  id: number
  name: string
  layout: string // JSON string: array of metric keys
  created_at: number
  updated_at: number
}

export const fetchDashboards = (s?: AbortSignal) =>
  api
    .get<{ dashboards: Dashboard[] }>('/admin/dashboards', s ? { signal: s } : undefined)
    .then((d) => d.dashboards ?? [])

export const createDashboard = (name: string, keys: string[]) =>
  api.post<{ ok: boolean; id: number }>('/admin/dashboards', { name, layout: JSON.stringify(keys) })

export const updateDashboard = (id: number, name: string, keys: string[]) =>
  api.put<{ ok: boolean }>(`/admin/dashboards/${id}`, { name, layout: JSON.stringify(keys) })

export const deleteDashboard = (id: number) => api.del<{ ok: boolean }>(`/admin/dashboards/${id}`)

/** Parse a stored layout JSON string into a metric-key array (fail-soft []). */
export function parseLayout(layout: string): string[] {
  try {
    const v = JSON.parse(layout)
    return Array.isArray(v) ? v.map(String) : []
  } catch {
    return []
  }
}

/* ── shared: live-metric catalog (A16.40) used by both screens ─────── */

export interface ReportMetric {
  key: string
  label: string
  value: number
  group: string
}

export const fetchReportMetrics = (s?: AbortSignal) =>
  api
    .get<{ metrics: ReportMetric[] }>('/admin/analytics/report', s ? { signal: s } : undefined)
    .then((d) => d.metrics ?? [])

export const ALERTS_DASH_STR = {
  // A2.10
  alertsTitle: 'Ogohlantirishlar markazi',
  alertsNote: 'Metrika chegaralari bo\'yicha ogohlantirish qoidalari (real registr). Baholash ishga tushirilganda qoidalar shu ro\'yxatdan olinadi.',
  addRule: 'Qoida qo\'shish',
  ruleName: 'Qoida nomi',
  metric: 'Metrika',
  threshold: 'Chegara',
  channel: 'Kanal',
  enabled: 'Yoqilgan',
  disabled: 'O\'chirilgan',
  current: 'Joriy qiymat',
  save: 'Saqlash',
  saved: 'Saqlandi',
  del: 'O\'chirish',
  deleted: 'O\'chirildi',
  failed: 'Xatolik',
  noRules: 'Hozircha qoida yo\'q',
  enable: 'Yoqish',
  disable: 'O\'chirish',
  chEmail: 'Email',
  chTelegram: 'Telegram',
  chWebhook: 'Webhook',
  chInapp: 'Ilova ichida',
  // A2.11
  dashTitle: 'Dashboard konstruktori',
  dashNote: 'Jonli metrikalardan nomli dashboard yig\'ing va saqlang (real config). Saqlangan dashboard tanlangan metrikalarning joriy qiymatlarini ko\'rsatadi.',
  newDashboard: 'Yangi dashboard',
  dashName: 'Dashboard nomi',
  pickMetrics: 'Metrikalarni tanlang',
  create: 'Yaratish',
  update: 'Yangilash',
  savedDashboards: 'Saqlangan dashboardlar',
  widgets: 'vidjet',
  open: 'Ochish',
  editing: 'Tahrirlanmoqda',
  cancel: 'Bekor',
  emptyPick: 'Kamida bitta metrika tanlang',
  noDashboards: 'Saqlangan dashboard yo\'q',
  loading: 'Yuklanmoqda…',
} as const
